import * as IoC from 'typescript-ioc';
import {
    IGenerateRoomsDTO,
    IImportOperatorsRequestParams,
    IRoomDetailsDTO,
    IRoomListDTO,
    IRoomNarrowed,
    IRoomNarrowedSortParams,
    IRoomSaveDTO,
    IRoomsDAO,
    IUpdateRoomDTO
} from '../rooms.dao';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { RetypePartial } from '../../infrastructure/type-extensions';
import { IEventRequestParams, IPaginationParams } from '../shared.dto';
import * as ROOMS_API from '../../constants/api/rooms-api.constants';
import { IControlRoomDTO } from '../control.dao';
import { JobDtoType } from '../../domain/jobs';
type IRoomDetailsDTOInternal = RetypePartial<IRoomDetailsDTO, 'startDateTime' | 'endDateTime', string>;

interface IEventMeetingsDTOInternal {
    content: IRoomDetailsDTOInternal[];
    totalElements: number;
}

class RoomsDAO implements IRoomsDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter) {
    }


    public async createRoom(accountId: number, eventId: number, createRoomDTO: IRoomSaveDTO) {
        await this.apiClient.post<IRoomSaveDTO>(ROOMS_API.CREATE_ROOM_URL, {
            accountId: accountId,
            eventId: eventId,
            ...createRoomDTO,
            startDateTime: this.apiDateConverter.dateToAPIString(createRoomDTO.startDateTime),
            endDateTime: this.apiDateConverter.dateToAPIString(createRoomDTO.endDateTime),
            connectionVersion: createRoomDTO.connectionVersion
        });
    }

    public async generateRooms(generateRoomsDTO: IGenerateRoomsDTO & IEventRequestParams) {
        return (await this.apiClient.post<JobDtoType>(ROOMS_API.GENERATE_ROOMS_URL, {
            ...generateRoomsDTO,
            startTime: this.apiDateConverter.dateToAPIString(generateRoomsDTO.startTime),
            endTime: this.apiDateConverter.dateToAPIString(generateRoomsDTO.endTime)
        })).parsedResponse;
    }


    public async getRoomById(params: { accountId: number; eventId: number; roomId: number }): Promise<IRoomDetailsDTO> {
        const response = await this.apiClient.get<IRoomDetailsDTOInternal>(ROOMS_API.GET_ROOM_URL, params);
        return {
            ...response.parsedResponse,
            startDateTime: this.apiDateConverter.apiStringToDate(response.parsedResponse.startDateTime),
            endDateTime: this.apiDateConverter.apiStringToDate(response.parsedResponse.endDateTime)
        };
    }

    public async updateRoom(accountId: number, eventId: number, roomId: number, updateRoomDTO: IRoomSaveDTO): Promise<void> {
        await this.apiClient.put<IRoomSaveDTO>(ROOMS_API.UPDATE_ROOM_URL, {
           accountId: accountId,
           eventId: eventId,
           roomId: roomId,
           ...updateRoomDTO,
            startDateTime: this.apiDateConverter.dateToAPIString(updateRoomDTO.startDateTime),
            endDateTime: this.apiDateConverter.dateToAPIString(updateRoomDTO.endDateTime)
        });
    }

    public async getRoomsList(params: IEventRequestParams & IPaginationParams & { query: string }): Promise<IRoomListDTO> {
        const response = await this.apiClient.get<IEventMeetingsDTOInternal>(ROOMS_API.ROOM_LIST_URL, {
            sort: ['startDateTime,asc'],
            ...params
        });
        return {
            ...response.parsedResponse,
            content: response.parsedResponse.content.map(room => (this.mapInternalRoomsDTOToPublicDTO(room)))

        };
    }

    public async getRoomsNarrowed(params: IEventRequestParams & IRoomNarrowedSortParams): Promise<IRoomNarrowed[]> {
        const sortingParams = params.startDateTime && params.endDateTime ? {
            startDateTime: this.apiDateConverter.dateToAPIString(params.startDateTime),
            endDateTime: this.apiDateConverter.dateToAPIString(params.endDateTime),
        } : {};
        const response = await this.apiClient.get<IRoomNarrowed[]>(ROOMS_API.ROOMS_NARROWED_URL, {
            ...params,
            ...sortingParams
        });
        return response.parsedResponse;
    }

    public async deleteRoom(params: { accountId: number; eventId: number; roomId: number }): Promise<void> {
        await this.apiClient.delete(ROOMS_API.DELETE_ROOM_URL, params);
    }

    public async createZoomConnections(params: IEventRequestParams & { zoomGroupId: number; additionalTopicText: string }) {
        const response = await this.apiClient.post<JobDtoType>(ROOMS_API.CREATE_ZOOM_CONNECTIONS_URL, params);
        return response.parsedResponse;
    }

    public async getOperatorsCSV(params: IEventRequestParams) {
        await this.apiClient.getFile(ROOMS_API.CSV_OPERATORS_DOWNLOAD_URL, params);
    }

    public async importOperators(params: IImportOperatorsRequestParams & IEventRequestParams) {
        const response = await this.apiClient.post<JobDtoType>(ROOMS_API.OPERATORS_IMPORT_URL, {
            accountId: params.accountId,
            eventId: params.eventId,
            file: params.file
        }, {
            ContentType: RequestContentType.formData,
        });
        return response.parsedResponse;
    }

    public async updateRoomInEvent(params: IEventRequestParams & IUpdateRoomDTO): Promise<IControlRoomDTO> {
        const response = await this.apiClient.put<IControlRoomDTO>(ROOMS_API.UPDATE_ROOM_IN_EVENT_URL, params);
        return response.parsedResponse;
    }

    private mapInternalRoomsDTOToPublicDTO(internalRoomDTO: IRoomDetailsDTOInternal) {
        return {
            ...internalRoomDTO,
            startDateTime: this.apiDateConverter.apiStringToDate(internalRoomDTO.startDateTime),
            endDateTime: this.apiDateConverter.apiStringToDate(internalRoomDTO.endDateTime),
        };
    }
}

export { RoomsDAO };
